/**
 * embeddingService.ts
 * Service for vector-based product matching using pre-computed embeddings.
 * 
 * This service loads product embeddings and provides similarity scoring
 * between user profiles and products using cosine similarity.
 */

import type { MasterProfile } from '../types/profile';
import { getAuthHeaders } from './apiAuth';
import { getApiBaseUrl } from '../utils/apiConfig';

// ============================================================================
// Types
// ============================================================================

export interface ProductEmbedding {
  productId: string;
  sku: string;
  title: string;
  embedding: number[];
  textUsed: string;
}

export interface SimilarityResult {
  productId: string;
  similarity: number;
  rank: number;
}

// ============================================================================
// Embedding Storage (loaded from JSON at runtime)
// ============================================================================

let productEmbeddings: ProductEmbedding[] = [];
let embeddingsLoaded = false;

// Cache for profile embeddings to avoid repeated API calls
const profileEmbeddingCache = new Map<string, { embedding: number[]; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Loads product embeddings from the JSON file.
 * Call this once at app initialization.
 */
export async function loadProductEmbeddings(): Promise<void> {
  if (embeddingsLoaded) return;
  
  try {
    // Dynamic import for the embeddings JSON
    const response = await fetch('/catalogs/product-embeddings.json');
    if (response.ok) {
      productEmbeddings = await response.json();
      embeddingsLoaded = true;
      console.log(`📊 Loaded ${productEmbeddings.length} product embeddings`);
    } else {
      console.warn('⚠️ Product embeddings not found, vector matching disabled');
    }
  } catch (error) {
    console.warn('⚠️ Failed to load product embeddings:', error);
  }
}

/**
 * Checks if embeddings are loaded and available.
 */
export function hasEmbeddings(): boolean {
  return embeddingsLoaded && productEmbeddings.length > 0;
}

/**
 * Gets the embedding for a specific product.
 */
export function getProductEmbedding(productId: string): number[] | null {
  const product = productEmbeddings.find(p => p.productId === productId);
  return product?.embedding || null;
}

// ============================================================================
// Vector Operations
// ============================================================================

/**
 * Calculates cosine similarity between two vectors.
 * Returns a value between -1 and 1, where 1 is identical.
 */
export function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length) {
    throw new Error(`Vector dimension mismatch: ${a.length} vs ${b.length}`);
  }
  
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  
  const magnitude = Math.sqrt(normA) * Math.sqrt(normB);
  return magnitude === 0 ? 0 : dotProduct / magnitude;
}

/**
 * Normalizes a value from a range to 0-1.
 */
function normalize(value: number, min: number, max: number): number {
  if (max === min) return 0.5;
  return (value - min) / (max - min);
}

// ============================================================================
// Profile → Embedding Conversion
// ============================================================================

/**
 * Creates a text representation of a user profile for embedding.
 * This text captures the user's skin needs and preferences.
 */
export function createProfileText(profile: MasterProfile): string {
  const parts: string[] = [];
  
  // Skin type
  if (profile.skin?.type) {
    parts.push(`Skin type: ${profile.skin.type}`);
  }
  
  // Concerns
  if (profile.skin?.concerns?.length) {
    parts.push(`Skin concerns: ${profile.skin.concerns.join(', ')}`);
  }
  
  // Goals
  if (profile.skin?.skinGoals?.length) {
    parts.push(`Skincare goals: ${profile.skin.skinGoals.join(', ')}`);
  }
  
  // Routine goals
  if (profile.skin?.routineGoals?.length) {
    parts.push(`Routine goals: ${profile.skin.routineGoals.join(', ')}`);
  }
  
  // Allergies/Avoids
  if (profile.skin?.allergies?.length) {
    parts.push(`Avoid ingredients: ${profile.skin.allergies.join(', ')}`);
  }
  
  // Age context
  if (profile.identity?.ageGroup) {
    parts.push(`Age group: ${profile.identity.ageGroup}`);
  }
  
  // Sensitivity
  const skinType = profile.skin?.type?.toLowerCase();
  if (skinType === 'sensitive') {
    parts.push('Needs gentle, low-irritation products');
  }
  
  return parts.join('. ');
}

/**
 * Creates a cache key from profile data.
 */
function getProfileCacheKey(profile: MasterProfile): string {
  const data = {
    skinType: profile.skin?.type,
    concerns: [...(profile.skin?.concerns || [])].sort().join(','),
    goals: [...(profile.skin?.skinGoals || [])].sort().join(','),
    allergies: [...(profile.skin?.allergies || [])].sort().join(','),
    ageGroup: profile.identity?.ageGroup,
  };
  return JSON.stringify(data);
}

/**
 * Fetches profile embedding from the backend API.
 * Uses caching to avoid repeated API calls for the same profile.
 * 
 * @param profile - User's master profile
 * @returns Embedding vector or null if unavailable
 */
export async function getProfileEmbedding(profile: MasterProfile): Promise<number[] | null> {
  if (!hasEmbeddings()) {
    return null; // No point if we don't have product embeddings
  }
  
  const cacheKey = getProfileCacheKey(profile);
  const now = Date.now();
  
  // Check cache
  const cached = profileEmbeddingCache.get(cacheKey);
  if (cached && now - cached.timestamp < CACHE_TTL) {
    console.log('📊 Using cached profile embedding');
    return cached.embedding;
  }
  
  try {
    const apiUrl = getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    const response = await fetch(`${apiUrl}/getProfileEmbedding`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        skinType: profile.skin?.type,
        concerns: profile.skin?.concerns,
        skinGoals: profile.skin?.skinGoals,
        routineGoals: profile.skin?.routineGoals,
        allergies: profile.skin?.allergies,
        ageGroup: profile.identity?.ageGroup,
      }),
    });
    
    if (!response.ok) {
      console.warn('Failed to get profile embedding:', response.status);
      return null;
    }
    
    const data = await response.json();
    const embedding = data.embedding as number[];
    
    // Cache the result
    profileEmbeddingCache.set(cacheKey, { embedding, timestamp: now });
    console.log(`📊 Generated profile embedding (${embedding.length} dims)`);
    
    return embedding;
  } catch (error) {
    console.warn('Error fetching profile embedding:', error);
    return null;
  }
}

// ============================================================================
// Similarity Scoring
// ============================================================================

/**
 * Calculates similarity scores between a user profile embedding and all products.
 * 
 * @param profileEmbedding - The user profile's embedding vector
 * @returns Array of products sorted by similarity (highest first)
 */
export function calculateProfileProductSimilarity(
  profileEmbedding: number[]
): SimilarityResult[] {
  if (!hasEmbeddings()) {
    return [];
  }
  
  const results: SimilarityResult[] = productEmbeddings.map(product => ({
    productId: product.productId,
    similarity: cosineSimilarity(profileEmbedding, product.embedding),
    rank: 0,
  }));
  
  // Sort by similarity descending
  results.sort((a, b) => b.similarity - a.similarity);
  
  // Assign ranks
  results.forEach((r, idx) => {
    r.rank = idx + 1;
  });
  
  return results;
}

/**
 * Converts similarity score to a scoring boost for the ranking algorithm.
 * Similarity ranges from ~0.3 to ~0.9 typically, we normalize to 0-0.4 boost.
 * 
 * @param similarity - Cosine similarity score (-1 to 1)
 * @returns Score boost (0 to 0.4)
 */
export function similarityToScoreBoost(similarity: number): number {
  // Typical similarity range for skincare products
  const minExpected = 0.3;
  const maxExpected = 0.85;
  
  // Normalize to 0-1 range
  const normalized = normalize(similarity, minExpected, maxExpected);
  
  // Clamp and scale to max boost of 0.4
  return Math.max(0, Math.min(0.4, normalized * 0.4));
}

/**
 * Gets embedding-based scores for all products given a profile.
 * Returns a map of productId → similarity boost.
 * 
 * This is the main function used by the ranking algorithm.
 */
export function getEmbeddingSimilarityMap(
  profileEmbedding: number[]
): Map<string, number> {
  const results = calculateProfileProductSimilarity(profileEmbedding);
  const map = new Map<string, number>();
  
  // Log raw similarities for diagnostics (dev only)
  if (import.meta.env.DEV && results.length > 0) {
    const debugLines = results.map(r =>
      `  ${r.rank}. ${r.productId}: sim=${r.similarity.toFixed(3)} → boost=${similarityToScoreBoost(r.similarity).toFixed(3)}`
    );
    console.log(`📊 RAG similarity scores:\n${debugLines.join('\n')}`);
  }
  
  for (const result of results) {
    map.set(result.productId, similarityToScoreBoost(result.similarity));
  }
  
  return map;
}

// ============================================================================
// Export
// ============================================================================

export default {
  loadProductEmbeddings,
  hasEmbeddings,
  getProductEmbedding,
  getProfileEmbedding,
  cosineSimilarity,
  createProfileText,
  calculateProfileProductSimilarity,
  similarityToScoreBoost,
  getEmbeddingSimilarityMap,
};
